import {
    DndContext,
    DragEndEvent,
    DragOverEvent,
    DragStartEvent,
    PointerSensor,
    UniqueIdentifier,
    closestCenter,
    useSensor,
    useSensors,
} from "@dnd-kit/core"
import { restrictToParentElement } from "@dnd-kit/modifiers"
import { SortableContext, arrayMove, verticalListSortingStrategy } from "@dnd-kit/sortable"
import { Fragment, useState } from "react"

import useTableView from "@organisms/ObjectsView/TableView/TableView.context"

import styles from "./ColumnOrdering.module.scss"
import { ColumnOrderingProps } from "./ColumnOrdering.types"
import ColumnOrderingDivider from "./ColumnOrderingDivider/ColumnOrderingDivider"
import ColumnOrderingItem from "./ColumnOrderingItem/ColumnOrderingItem"

export default function ColumnOrdering(props: ColumnOrderingProps) {
    const { isVisible: isContainerVisible } = props

    const {
        currentTableViewConfig,
        updateColumnOrder,
        pinColumn,
        unpinColumn,
        updateColumnPinningOrder,
        pinnedColumns,
    } = useTableView()

    const isColumnVisible = (columnId: string) => currentTableViewConfig?.columnVisibility?.[columnId] !== false

    const fullColumnOrder = currentTableViewConfig?.columnOrder ?? []
    const pinnedSet = new Set<string>(pinnedColumns)
    const allUnpinnedColumnIds = fullColumnOrder.filter((id) => !pinnedSet.has(id))
    const allRenderedColumnIds = [...pinnedColumns, ...allUnpinnedColumnIds]

    const [activeId, setActiveId] = useState<UniqueIdentifier | null>(null)
    const [overId, setOverId] = useState<UniqueIdentifier | null>(null)

    const getColumnIndex = (id: UniqueIdentifier) => allRenderedColumnIds.indexOf(id as string)

    const activeIndex = activeId ? getColumnIndex(activeId) : -1

    const getDropIndicatorInfo = (items: string[]) => {
        if (!activeId || !overId || activeId === overId) {
            return null
        }
        const activeIdx = items.indexOf(activeId as string)
        const overIdx = items.indexOf(overId as string)
        if (activeIdx === -1 || overIdx === -1) {
            return null
        }
        return {
            overIdx,
            position: activeIdx < overIdx ? ("after" as const) : ("before" as const),
        }
    }

    const pinnedIndicator = getDropIndicatorInfo(pinnedColumns)
    const unpinnedIndicator = getDropIndicatorInfo(allUnpinnedColumnIds)

    const columnIsPinned = (columnId: string) => {
        return (currentTableViewConfig?.columnPinning.left ?? []).includes(columnId)
    }

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 8,
            },
        }),
    )

    const onDragStart = ({ active }: DragStartEvent) => {
        if (!active) {
            return
        }

        setActiveId(active.id)
    }

    const onDragOver = ({ over }: DragOverEvent) => {
        setOverId(over?.id ?? null)
    }

    const onDragEnd = ({ over }: DragEndEvent) => {
        setActiveId(null)
        setOverId(null)

        if (over) {
            const overIndex = getColumnIndex(over.id)
            const isPinned = columnIsPinned(over.id as string)

            if (isPinned) {
                const newColumnPinningOrder = arrayMove(pinnedColumns, activeIndex, overIndex)
                updateColumnPinningOrder(newColumnPinningOrder)
            } else if (activeIndex !== overIndex) {
                const newOrder = arrayMove(allRenderedColumnIds, activeIndex, overIndex)
                updateColumnOrder(newOrder)
            }
        }
    }

    const onPinColumn = (columnId: string) => {
        pinColumn(columnId)
    }

    const onUnpinColumn = (columnId: string) => {
        unpinColumn(columnId)
    }

    const hasPinnedColumns = pinnedColumns.length > 0

    return (
        <DndContext
            collisionDetection={closestCenter}
            onDragStart={onDragStart}
            onDragOver={onDragOver}
            sensors={sensors}
            onDragEnd={onDragEnd}
            modifiers={[restrictToParentElement]}
        >
            <div className={styles.base} data-vaul-no-drag={true}>
                {hasPinnedColumns && (
                    <>
                        <div className={styles.pinnedArea}>
                            <SortableContext items={pinnedColumns} strategy={verticalListSortingStrategy}>
                                {pinnedColumns.map((columnId, idx) => {
                                    return (
                                        <Fragment key={columnId}>
                                            {pinnedIndicator?.position === "before" &&
                                                pinnedIndicator.overIdx === idx && (
                                                    <div className={styles.dropIndicator} />
                                                )}
                                            <ColumnOrderingItem
                                                isTabbable={isContainerVisible}
                                                isVisible={isColumnVisible(columnId)}
                                                columnId={columnId}
                                                onPinColumn={onPinColumn}
                                                onUnpinColumn={onUnpinColumn}
                                                isPinned={true}
                                            />
                                            {pinnedIndicator?.position === "after" &&
                                                pinnedIndicator.overIdx === idx && (
                                                    <div className={styles.dropIndicator} />
                                                )}
                                        </Fragment>
                                    )
                                })}
                            </SortableContext>
                        </div>
                        <ColumnOrderingDivider label="Unpinned" />
                    </>
                )}
                <div className={styles.unpinnedArea}>
                    <SortableContext items={allUnpinnedColumnIds} strategy={verticalListSortingStrategy}>
                        {allUnpinnedColumnIds.map((columnId, idx) => {
                            return (
                                <Fragment key={columnId}>
                                    {unpinnedIndicator?.position === "before" && unpinnedIndicator.overIdx === idx && (
                                        <div className={styles.dropIndicator} />
                                    )}
                                    <ColumnOrderingItem
                                        isTabbable={isContainerVisible}
                                        isVisible={isColumnVisible(columnId)}
                                        columnId={columnId}
                                        onPinColumn={onPinColumn}
                                        onUnpinColumn={onUnpinColumn}
                                        isPinned={false}
                                    />
                                    {unpinnedIndicator?.position === "after" && unpinnedIndicator.overIdx === idx && (
                                        <div className={styles.dropIndicator} />
                                    )}
                                </Fragment>
                            )
                        })}
                    </SortableContext>
                </div>
            </div>
        </DndContext>
    )
}
